/**
 * BrandedPlayer — V1 stub with custom chrome.
 *
 * Renders a <video> (driven by video.js + VHS via useVideoJsVideo, with
 * controls:false) for the active branded clip plus:
 *   - IntroOverlay (player metadata card; first 5s)
 *   - EndcardOverlay (sponsor rectangle + auto-advance countdown; on ended)
 *   - TopBannerSlot (floating sponsor pill, slides in mid-clip)
 *   - SponsorWatermark UL (always-on while playing; LR is part of PlayerChrome)
 *   - PlayerChrome (custom controls bar at the bottom)
 *
 * Intentionally NOT RAMP / GAM — kept minimal (no ads stack) so we can iterate
 * on branded UX without touching the partner-shared player. We do reuse
 * video.js purely as the HLS/VHS playback engine; all chrome is custom.
 */
import { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";

import { IntroOverlay } from "../IntroOverlay";
import { EndcardOverlay } from "../EndcardOverlay";
import { SponsorLogo } from "../SponsorAssets";
import { TopBannerSlot } from "../TopBannerSlot";
import {
  BrandedActiveClipState,
  BrandedFullscreenState,
  BrandedLayoutVariantState,
  BrandedSponsorConfigState,
  BrandedViewerGeoState,
} from "../../State";
import { usePlaybackHandlers } from "../../ViewModel";
import {
  getClipHlsUrl,
  getClipMp4Url,
  getClipThumbnail,
} from "../../metadataHelpers";
import { useVideoJsVideo } from "./useVideoJsVideo";
import { ALLSTAR_FALLBACK_UPPER_LEFT } from "../../sponsors";
import { countClipImpression } from "MVVM/Utilities/Clip";
import { analytics } from "MVVM/Analytics";
import { fireAdClickBeacon, useImpressionBeacon } from "../../tracking";
import { PlayerChrome } from "./PlayerChrome";
import { useVideoControls } from "./ViewModel";
import {
  PlayerFrame,
  PlayerVideo,
  SponsorAnchor,
  SponsorLogoLink,
} from "./styledComponents";

interface BrandedPlayerProps {
  platform: string;
  playlist: string;
}

export const BrandedPlayer = ({ platform, playlist }: BrandedPlayerProps) => {
  const activeClip = useRecoilValue(BrandedActiveClipState);
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const isFullscreen = useRecoilValue(BrandedFullscreenState);
  const viewerGeo = useRecoilValue(BrandedViewerGeoState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const { onTimeUpdate, onEnded } = usePlaybackHandlers();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Clip-view tally. Mirrors the standalone EmbeddablePlayer, which fires
  // `countClipImpression(clip.shareId)` on play start. The video.js player
  // here doesn't go through the shared VideoPlayer's `onPlayStart`, so we
  // fire it ourselves on the play event — deduped by shareId so it counts
  // once per clip (not on every pause/resume), and re-arms when the active
  // clip changes.
  const countedShareIdRef = useRef<string | null>(null);

  // Latest analytics context, captured each render and read at play-fire
  // time. Mirrors tracking.ts's `latestRef` pattern: the play listener is
  // attached per clip, so reading these from a ref (rather than the closure)
  // keeps the "Video Player - Play" payload current — without forcing the
  // listener set to re-bind every time the sponsor/geo/layout resolves.
  const playContextRef = useRef({
    activeClip,
    sponsor,
    viewerGeo,
    layoutVariant,
    platform,
    playlist,
  });
  playContextRef.current = {
    activeClip,
    sponsor,
    viewerGeo,
    layoutVariant,
    platform,
    playlist,
  };

  // Source selection: prefer the HLS manifest (clip.hls, shipped on every
  // branded clip), fall back to progressive MP4. useVideoJsVideo owns the
  // attach + per-clip playback reset (currentTime = 0 + play()) so the
  // source swap and restart happen atomically when the active clip changes.
  const hlsUrl = getClipHlsUrl(activeClip);
  const mp4Url = getClipMp4Url(activeClip);
  const hasSource = !!(hlsUrl || mp4Url);
  const poster = getClipThumbnail(activeClip);

  // Playback engine: video.js + VHS, with controls:false so our custom
  // PlayerChrome and overlays render on top. Owns source selection and the
  // fullscreen HD upgrade (hard HD-only reload + seek-back; video.js shows
  // its own loading spinner over the reload).
  const playerRef = useVideoJsVideo(videoRef, {
    hlsUrl,
    mp4Url,
    isFullscreen,
    reattachKey: activeClip?._id,
  });
  const controls = useVideoControls(playerRef, activeClip?._id);
  const { togglePlay } = controls;

  // All remaining playback wiring (Recoil time/ended pumps for the ad-slot
  // phase machine, impression tally, tap-to-toggle) binds to the video.js
  // player — NOT to the React-rendered <video> — because on iOS video.js
  // clones the element it adopts and plays through the clone, so listeners
  // and JSX event props on the original element never fire there.
  const shareId = activeClip?.shareId;
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return undefined;

    const handleTime = () => onTimeUpdate(player.currentTime());
    const handlePlay = () => {
      if (!shareId || countedShareIdRef.current === shareId) return;
      countedShareIdRef.current = shareId;
      countClipImpression(shareId);

      // Fire once per clip play (deduped above by shareId, re-armed when the
      // active clip changes). `warehouseOnly` keeps this branded-embed signal
      // out of the product funnels — it lands in the warehouse only, matching
      // the other partner-iframe events (see Analytics PageViewTracker and
      // useAnalyticsContext).
      const {
        activeClip: clip,
        sponsor: sp,
        viewerGeo: geo,
        layoutVariant: layout,
        platform: plat,
        playlist: pl,
      } = playContextRef.current;
      analytics.track("Video Player - Play", {
        clipId: clip?.internalId,
        clipShareId: clip?.shareId,
        clipTitle: clip?.title,
        game: clip?.game,
        userId: clip?.user,
        username: clip?.username,
        views: clip?.views,
        location: "Branded Embeddable Player",
        layoutVariant: layout,
        viewerGeo: geo,
        campaignId: sp?.campaignId,
        brandName: sp?.brandName,
        platform: plat,
        playlist: pl,
        referrer: document.referrer,
        warehouseOnly: true,
      });
    };
    // Tap/click anywhere on the playback surface (the video.js wrapper)
    // toggles play/pause. PlayerChrome and the sponsor overlays are siblings
    // of the wrapper, so their clicks never bubble through here; the intro
    // scrim is pointer-events:none, so its taps fall through to this.
    const surface = player.el();

    player.on("timeupdate", handleTime);
    player.on("play", handlePlay);
    player.on("ended", onEnded);
    surface.addEventListener("click", togglePlay);

    return () => {
      surface.removeEventListener("click", togglePlay);
      if (player.isDisposed()) return;
      player.off("timeupdate", handleTime);
      player.off("play", handlePlay);
      player.off("ended", onEnded);
    };
  }, [playerRef, shareId, onTimeUpdate, onEnded, togglePlay]);

  /**
   * Upper-left watermark: prefer the resolved sponsor's creative, fall
   * back to our own Allstar mark when no campaign is live for this geo.
   * Slot is never empty in V1 — every embed surfaces Allstar attribution
   * at minimum, with paid campaigns taking priority over the default.
   */
  const upperLeftCreative =
    sponsor?.slots.upperLeftLogo ?? ALLSTAR_FALLBACK_UPPER_LEFT;

  // Impression beacon for the UL watermark. Always-on; the watermark is
  // visible from t=0 of every clip, so this fires immediately on mount and
  // again whenever the active clip changes (clipId is part of the dedup key).
  const upperLeftImpressionRef = useImpressionBeacon({
    slot: "upperLeftLogo",
    creativeId: upperLeftCreative.id,
  });

  const handleUpperLeftClick = () => {
    fireAdClickBeacon({
      campaignId: sponsor?.campaignId,
      slot: "upperLeftLogo",
      creativeId: upperLeftCreative.id,
      viewerGeo,
    });
  };

  return (
    <PlayerFrame $bottomPlaylist={isBottomPlaylist} data-branded-frame>
      {hasSource && (
        // No `src` attr and no event props — video.js (via useVideoJsVideo)
        // owns the element and assigns the source (HLS through VHS, or MP4
        // fallback), so React doesn't fight video.js's MediaSource
        // attachment. On iOS video.js even swaps in a clone of this element,
        // which is why every playback event is wired through the player
        // instance above instead of JSX props here.
        <PlayerVideo
          ref={videoRef}
          poster={poster}
          autoPlay
          muted
          playsInline
        />
      )}
      <IntroOverlay />
      <EndcardOverlay />
      <TopBannerSlot />
      <SponsorAnchor
        $position="ul"
        $fs={isFullscreen}
        $bp={isBottomPlaylist}
        ref={upperLeftImpressionRef}
      >
        <SponsorLogoLink
          href={upperLeftCreative.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={upperLeftCreative.altText}
          onClick={handleUpperLeftClick}
        >
          {/* Upper-left wordmark scales with the layout variant. In
              bottom-playlist mode the player runs at full iframe width
              so there's room for a larger watermark, AND we want the
              UL to read as the dominant brand mark (paired with the
              shrunken LR in PlayerChrome — together they shift attention
              upper-left as Nick called out). 78 → 110 is the same
              ~40% bump the LR loses (16 → 12). Side-rail keeps 78. */}
          <SponsorLogo
            creative={upperLeftCreative}
            variant="wordmark"
            width={isBottomPlaylist ? 110 : 78}
          />
        </SponsorLogoLink>
      </SponsorAnchor>
      <PlayerChrome
        playing={controls.playing}
        muted={controls.muted}
        volume={controls.volume}
        currentSec={controls.currentSec}
        durationSec={controls.durationSec}
        onTogglePlay={togglePlay}
        onToggleMute={controls.toggleMute}
        onSetVolume={controls.setVolume}
        onSeek={controls.seekToFraction}
        onToggleFullscreen={controls.toggleFullscreen}
        sponsor={sponsor}
        bottomPlaylist={isBottomPlaylist}
      />
    </PlayerFrame>
  );
};
